/**
 * Persistent two-tier formatting ribbon for the clinical document editor.
 *
 * Tier 2 of the unified Office Ribbon for clinical document editing:
 * - History: Undo, Redo
 * - Zoom: 50% to 200% canvas scaling selector
 * - Typography: Paragraph style (p, h1, h2, h3), Font family (Inter, Arial, Calibri, Merriweather, JetBrains Mono)
 * - Stepper: Font size stepper (-, size, +)
 * - Text formatting: Bold, Italic, Underline, Strikethrough, Clear Formatting
 * - Palettes: Text color dropdown, Highlight color dropdown
 * - Alignment: Left, Center, Right
 * - Lists & Indentation: Bulleted list, Numbered list, Increase/Decrease Indent
 * - Insert menu: Add Comment on selection, Source Citation (†), Clinical Inference (‡)
 */
import React, { useState } from "react";
import type { TipTapEditorHandle } from "./TipTapEditor";

/** Props for {@link DocumentRibbon}. */
export interface DocumentRibbonProps {
  /** Active editor handle to drive formatting on the currently active section. */
  editorHandle: TipTapEditorHandle | null;
  /** Whether editing is disabled globally. */
  disabled?: boolean;
  /** Current zoom level percentage (default: 100). */
  zoom?: number;
  /** Callback when zoom level changes. */
  onZoomChange?: (zoom: number) => void;
}

const TEXT_COLORS = [
  { label: "Default", color: "#0f172a" },
  { label: "Blue", color: "#2563eb" },
  { label: "Emerald", color: "#059669" },
  { label: "Amber", color: "#d97706" },
  { label: "Rose", color: "#e11d48" },
  { label: "Purple", color: "#9333ea" },
  { label: "Indigo", color: "#4f46e5" },
  { label: "Teal", color: "#0d9488" },
  { label: "Slate", color: "#64748b" },
  { label: "Inherit", color: "inherit" },
];

const HIGHLIGHT_COLORS = [
  { label: "Yellow", color: "#fef08a" },
  { label: "Green", color: "#bbf7d0" },
  { label: "Blue", color: "#bfdbfe" },
  { label: "Purple", color: "#e9d5ff" },
  { label: "Pink", color: "#fbcfe8" },
  { label: "Orange", color: "#fed7aa" },
  { label: "Gray", color: "#e2e8f0" },
  { label: "None", color: "transparent" },
];

const FONT_FAMILIES = [
  { label: "Prata (Serif)", value: "Prata" },
  { label: "Inter (Sans)", value: "Inter" },
  { label: "Arial", value: "Arial" },
  { label: "Calibri", value: "Calibri" },
  { label: "Merriweather", value: "Merriweather" },
  { label: "JetBrains Mono", value: "JetBrains Mono" },
];

export const DocumentRibbon: React.FC<DocumentRibbonProps> = ({
  editorHandle,
  disabled = false,
  zoom = 100,
  onZoomChange,
}) => {
  const currentFontSize = editorHandle?.currentFontSize ?? 11;
  const [draftInput, setDraftInput] = useState<string | null>(null);
  const displayValue =
    draftInput !== null ? draftInput : String(currentFontSize);
  const [textColorOpen, setTextColorOpen] = useState(false);
  const [highlightColorOpen, setHighlightColorOpen] = useState(false);
  const [insertOpen, setInsertOpen] = useState(false);

  const canUndo = Boolean(editorHandle?.canUndo) && !disabled;
  const canRedo = Boolean(editorHandle?.canRedo) && !disabled;

  const currentHeading = editorHandle?.currentHeading ?? "p";
  const currentFontFamily = editorHandle?.currentFontFamily ?? "Inter";
  const currentTextAlign = editorHandle?.currentTextAlign ?? "left";
  const isBold = Boolean(editorHandle?.isBold);
  const isItalic = Boolean(editorHandle?.isItalic);
  const isUnderline = Boolean(editorHandle?.isUnderline);
  const isStrike = Boolean(editorHandle?.isStrike);
  const isBulletList = Boolean(editorHandle?.isBulletList);
  const isOrderedList = Boolean(editorHandle?.isOrderedList);
  const currentColor = editorHandle?.currentColor ?? "#0f172a";
  const currentHighlight = editorHandle?.currentHighlight ?? "transparent";

  const handleUndo = (): void => {
    editorHandle?.undo();
  };

  const handleRedo = (): void => {
    editorHandle?.redo();
  };

  const handleStyleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const val = e.target.value;
    if (val === "h1") editorHandle?.setHeading(1);
    else if (val === "h2") editorHandle?.setHeading(2);
    else if (val === "h3") editorHandle?.setHeading(3);
    else editorHandle?.setHeading("p");
  };

  const handleFontFamilyChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    editorHandle?.setFontFamily(e.target.value);
  };

  const handleFontSizeApply = (newSize: number): void => {
    const clamped = Math.max(8, Math.min(36, newSize));
    setDraftInput(null);
    editorHandle?.setFontSize(clamped);
  };

  const handleSizeChange = (delta: number): void => {
    handleFontSizeApply(currentFontSize + delta);
  };

  const handleInsert = (kind: "src" | "inf" | "comment"): void => {
    setInsertOpen(false);
    if (kind === "comment") {
      editorHandle?.addCommentOnSelection();
    } else if (kind === "src") {
      const ref = window.prompt(
        "Enter citation reference (e.g. NPE-2026-08-24):",
      );
      if (ref?.trim()) {
        editorHandle?.insertCitation(ref.trim());
      }
    } else {
      const cards = window.prompt(
        "Enter contributing card IDs or names (comma-separated):",
      );
      if (cards?.trim()) {
        editorHandle?.insertInference(cards.trim());
      }
    }
  };

  return (
    <div
      data-testid="document-ribbon"
      className="relative z-20 bg-slate-50 border-b border-slate-200 px-3 py-1.5 min-h-[44px] flex items-center flex-wrap gap-1 shrink-0 text-xs"
    >
      {/* Undo / Redo */}
      <button
        type="button"
        data-testid="ribbon-btn-undo"
        disabled={!canUndo}
        onClick={handleUndo}
        onMouseDown={(e) => e.preventDefault()}
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
        className="inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-40 transition-colors"
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4"
          />
        </svg>
      </button>
      <button
        type="button"
        data-testid="ribbon-btn-redo"
        disabled={!canRedo}
        onClick={handleRedo}
        onMouseDown={(e) => e.preventDefault()}
        title="Redo (Ctrl+Y)"
        aria-label="Redo"
        className="inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-40 transition-colors"
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 10H11a5 5 0 00-5 5v2m15-7l-4-4m4 4l-4 4"
          />
        </svg>
      </button>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Zoom Selector */}
      <select
        data-testid="ribbon-select-zoom"
        value={zoom}
        disabled={disabled}
        onChange={(e) => onZoomChange?.(Number(e.target.value))}
        aria-label="Zoom"
        className="h-8 text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-blue-500"
      >
        <option value={50}>50%</option>
        <option value={75}>75%</option>
        <option value={90}>90%</option>
        <option value={100}>100%</option>
        <option value={125}>125%</option>
        <option value={150}>150%</option>
        <option value={200}>200%</option>
      </select>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Paragraph Style Selector */}
      <select
        data-testid="ribbon-select-style"
        value={currentHeading}
        disabled={disabled || !editorHandle}
        onChange={handleStyleChange}
        aria-label="Paragraph Style"
        className="h-8 text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-blue-500"
      >
        <option value="p">Normal text</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </select>

      {/* Font Family Selector */}
      <select
        data-testid="ribbon-select-font"
        value={currentFontFamily}
        disabled={disabled || !editorHandle}
        onChange={handleFontFamilyChange}
        aria-label="Font Family"
        className="h-8 text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-blue-500"
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Font Size Stepper & Input */}
      <div className="flex items-center bg-white border border-slate-300 rounded-md h-8 px-1.5">
        <button
          type="button"
          data-testid="ribbon-btn-size-down"
          disabled={disabled || !editorHandle}
          onClick={() => handleSizeChange(-1)}
          aria-label="Decrease font size"
          className="px-1.5 text-slate-500 hover:text-slate-900 font-bold text-sm disabled:opacity-40"
        >
          −
        </button>
        <input
          type="number"
          min={8}
          max={36}
          data-testid="ribbon-font-size-input"
          aria-label="Font Size"
          disabled={disabled || !editorHandle}
          value={displayValue}
          onChange={(e) => {
            setDraftInput(e.target.value);
            const parsed = parseInt(e.target.value, 10);
            if (!Number.isNaN(parsed)) {
              editorHandle?.setFontSize(Math.max(8, Math.min(36, parsed)));
            }
          }}
          onBlur={() => {
            if (draftInput === null) return;
            const parsed = parseInt(draftInput, 10);
            if (Number.isNaN(parsed)) {
              setDraftInput(null);
            } else {
              handleFontSizeApply(parsed);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="w-9 text-center text-xs font-semibold text-slate-800 bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          type="button"
          data-testid="ribbon-btn-size-up"
          disabled={disabled || !editorHandle}
          onClick={() => handleSizeChange(1)}
          aria-label="Increase font size"
          className="px-1.5 text-slate-500 hover:text-slate-900 font-bold text-sm disabled:opacity-40"
        >
          +
        </button>
      </div>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Bold / Italic / Underline / Strike */}
      <button
        type="button"
        data-testid="ribbon-btn-bold"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.toggleBold()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Bold"
        aria-pressed={isBold}
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-sm font-bold transition-colors ${
          isBold
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        B
      </button>
      <button
        type="button"
        data-testid="ribbon-btn-italic"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.toggleItalic()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Italic"
        aria-pressed={isItalic}
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-sm italic font-serif transition-colors ${
          isItalic
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        I
      </button>
      <button
        type="button"
        data-testid="ribbon-btn-underline"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.toggleUnderline()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Underline"
        aria-pressed={isUnderline}
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-sm underline transition-colors ${
          isUnderline
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        U
      </button>
      <button
        type="button"
        data-testid="ribbon-btn-strike"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.toggleStrike()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Strikethrough"
        aria-pressed={isStrike}
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-sm line-through transition-colors ${
          isStrike
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        S
      </button>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Text Color Dropdown */}
      <div className="relative">
        <button
          type="button"
          data-testid="ribbon-btn-text-color"
          disabled={disabled || !editorHandle}
          onClick={() => setTextColorOpen((prev) => !prev)}
          onMouseDown={(e) => e.preventDefault()}
          aria-label="Text color"
          title="Text Color"
          className="inline-flex flex-col items-center justify-center h-8 min-w-8 w-8 rounded-md hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <span className="font-bold text-xs">A</span>
          <div
            className="w-4 h-1 rounded-xs"
            style={{
              backgroundColor:
                currentColor === "inherit" ? "#0f172a" : currentColor,
            }}
          />
        </button>
        {textColorOpen && (
          <div
            data-testid="ribbon-palette-text-color"
            className="absolute left-0 mt-1 z-30 grid grid-cols-5 gap-1.5 p-2 bg-white rounded-lg border border-slate-200 shadow-lg w-40"
          >
            {TEXT_COLORS.map((tc) => (
              <button
                key={tc.label}
                type="button"
                data-testid={`ribbon-color-option-${tc.label.toLowerCase()}`}
                title={tc.label}
                onClick={() => {
                  editorHandle?.setColor(tc.color);
                  setTextColorOpen(false);
                }}
                className={`w-5 h-5 rounded cursor-pointer border ${
                  tc.color === "inherit"
                    ? "border-slate-300 bg-white"
                    : "border-transparent"
                }`}
                style={{
                  backgroundColor:
                    tc.color === "inherit" ? "#ffffff" : tc.color,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Highlight Color Dropdown */}
      <div className="relative">
        <button
          type="button"
          data-testid="ribbon-btn-highlight"
          disabled={disabled || !editorHandle}
          onClick={() => setHighlightColorOpen((prev) => !prev)}
          onMouseDown={(e) => e.preventDefault()}
          aria-label="Highlight color"
          title="Highlight Color"
          className="inline-flex flex-col items-center justify-center h-8 min-w-8 w-8 rounded-md hover:bg-slate-200 text-slate-700 disabled:opacity-40"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
          <div
            className="w-4 h-1 rounded-xs"
            style={{
              backgroundColor:
                currentHighlight === "transparent"
                  ? "#fef08a"
                  : currentHighlight,
            }}
          />
        </button>
        {highlightColorOpen && (
          <div
            data-testid="ribbon-palette-highlight"
            className="absolute left-0 mt-1 z-30 grid grid-cols-4 gap-1.5 p-2 bg-white rounded-lg border border-slate-200 shadow-lg w-36"
          >
            {HIGHLIGHT_COLORS.map((hc) => (
              <button
                key={hc.label}
                type="button"
                data-testid={`ribbon-highlight-option-${hc.label.toLowerCase()}`}
                title={hc.label}
                onClick={() => {
                  editorHandle?.setHighlight(hc.color);
                  setHighlightColorOpen(false);
                }}
                className="w-6 h-6 rounded border border-slate-300 flex items-center justify-center text-[10px] font-bold cursor-pointer"
                style={{ backgroundColor: hc.color }}
              >
                {hc.color === "transparent" ? "∅" : ""}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Clear Formatting */}
      <button
        type="button"
        data-testid="ribbon-btn-clear-format"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.clearFormatting()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Clear formatting"
        title="Clear Formatting"
        className="inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-slate-700 hover:bg-slate-200 disabled:opacity-40 transition-colors"
      >
        <span className="font-serif font-bold text-sm leading-none flex items-center justify-center">
          <span>T</span>
          <span className="text-xs text-slate-500 font-sans -ml-0.5">x</span>
        </span>
      </button>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Alignment */}
      <button
        type="button"
        data-testid="ribbon-align-left"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.setTextAlign("left")}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Align left"
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md transition-colors ${
          currentTextAlign === "left"
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 6h16M4 12h10M4 18h16"
          />
        </svg>
      </button>
      <button
        type="button"
        data-testid="ribbon-align-center"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.setTextAlign("center")}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Align center"
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md transition-colors ${
          currentTextAlign === "center"
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 6h16M7 12h10M4 18h16"
          />
        </svg>
      </button>
      <button
        type="button"
        data-testid="ribbon-align-right"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.setTextAlign("right")}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Align right"
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md transition-colors ${
          currentTextAlign === "right"
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 6h16M10 12h10M4 18h16"
          />
        </svg>
      </button>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Lists */}
      <button
        type="button"
        data-testid="ribbon-list-bullet"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.toggleBulletList()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Bullet list"
        title="Bullet List"
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md transition-colors ${
          isBulletList
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 6h.01M8 6h12M4 12h.01M8 12h12M4 18h.01M8 18h12"
          />
        </svg>
      </button>
      <button
        type="button"
        data-testid="ribbon-list-ordered"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.toggleOrderedList()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Numbered list"
        title="Numbered List"
        className={`inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md transition-colors ${
          isOrderedList
            ? "bg-slate-200 text-blue-700"
            : "text-slate-700 hover:bg-slate-200"
        } disabled:opacity-40`}
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 6h13M7 12h13M7 18h13M3 6h1v3H3m0 6h2l-2 2h2m-2 4h1a1 1 0 001-1v-1a1 1 0 00-1-1H3"
          />
        </svg>
      </button>

      {/* Indent / Outdent */}
      <button
        type="button"
        data-testid="ribbon-btn-outdent"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.outdent()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Decrease indent"
        title="Decrease Indent"
        className="inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-xs text-slate-700 hover:bg-slate-200 disabled:opacity-40 transition-colors"
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M11 19h9M11 5h9M11 12h9M7 8l-4 4 4 4"
          />
        </svg>
      </button>
      <button
        type="button"
        data-testid="ribbon-btn-indent"
        disabled={disabled || !editorHandle}
        onClick={() => editorHandle?.indent()}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Increase indent"
        title="Increase Indent"
        className="inline-flex items-center justify-center h-8 min-w-8 w-8 rounded-md text-xs text-slate-700 hover:bg-slate-200 disabled:opacity-40 transition-colors"
      >
        <svg
          className="w-4.5 h-4.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M11 19h9M11 5h9M11 12h9M3 8l4 4-4 4"
          />
        </svg>
      </button>

      <div className="w-[1px] h-6 bg-slate-200 mx-1" aria-hidden="true" />

      {/* Insert Menu */}
      <div className="relative">
        <button
          type="button"
          data-testid="ribbon-insert-dropdown"
          disabled={disabled || !editorHandle}
          onClick={() => setInsertOpen((prev) => !prev)}
          onMouseDown={(e) => e.preventDefault()}
          aria-label="Insert"
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-40"
        >
          <svg
            className="w-3 h-3 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Insert</span>
          <span aria-hidden="true" className="text-slate-400 text-[10px]">
            ▾
          </span>
        </button>
        {insertOpen && (
          <div
            data-testid="ribbon-insert-menu"
            className="absolute left-0 mt-1 z-30 w-52 bg-white rounded-lg border border-slate-200 shadow-lg py-1.5 text-xs"
          >
            <button
              type="button"
              data-testid="ribbon-insert-comment"
              onClick={() => handleInsert("comment")}
              className="w-full px-3 py-1.5 text-left hover:bg-amber-50 flex flex-col items-start gap-0.5"
            >
              <div className="flex items-center gap-1.5 text-amber-700 font-medium">
                <svg
                  className="w-3.5 h-3.5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
                <span>Add Comment</span>
              </div>
              <span className="text-[10px] text-slate-500">
                Comment on selected text
              </span>
            </button>
            <div className="my-1 border-t border-slate-100" />
            <div className="px-3 py-1 text-[10px] uppercase font-semibold text-slate-400 tracking-wider">
              Clinical Grounding
            </div>
            <button
              type="button"
              data-testid="ribbon-insert-citation"
              onClick={() => handleInsert("src")}
              className="w-full px-3 py-1.5 text-left hover:bg-blue-50 flex flex-col items-start gap-0.5"
            >
              <div className="flex items-center gap-1.5 text-blue-700 font-medium">
                <sup className="text-blue-500 font-medium text-xs">†</sup>
                <span>Source Citation</span>
              </div>
              <span className="text-[10px] text-slate-500">
                Link to raw encounter or assessment
              </span>
            </button>
            <button
              type="button"
              data-testid="ribbon-insert-inference"
              onClick={() => handleInsert("inf")}
              className="w-full px-3 py-1.5 text-left hover:bg-purple-50 flex flex-col items-start gap-0.5"
            >
              <div className="flex items-center gap-1.5 text-purple-700 font-medium">
                <sup className="text-violet-500 font-medium text-xs">‡</sup>
                <span>Clinical Inference</span>
              </div>
              <span className="text-[10px] text-slate-500">
                Mark synthesized finding across cards
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentRibbon;
